import * as React from 'react';
import {
    AcceptIcon,
    Accordion,
    Button,
    ChevronDownIcon,
    ChevronEndIcon,
    CloseIcon,
    Dialog,
    EditIcon,
    FilesPictureColoredIcon,
    Flex,
    FlexItem,
    ListItem,
    Loader,
    MenuButton,
    MoreIcon,
    Text,
    TrashCanIcon
} from '@fluentui/react-northstar';
import {ReactComponent as FolderIcon} from "../../assets/msFolder.svg";
import EditInput from "../Utils/EditInput";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import {blobsRootFolderName, deleteBlobFolder} from "../../apis/blobImageApi";
import {ImageGallery} from '../../Interfaces/ImageGallery';
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";

/**
 * Sharepoint picker props
 */
interface ISharepointPickerProps {
    // Available items
    items: Array<string>,
    // Allowed files extensions
    extensions?: Array<string>
    // Handle item selection
    handleSelect?: (item: string, fileNames: Array<string>) => void,
    // Selected file
    selectedFile?: ImageGallery | undefined,
    // Selected folder
    selectedFolder?: string,
    // Open image picker
    onOpenImagePicker?: () => void,
    // Select only folders
    onlyFolders?: boolean,
    // Custom styles
    styles?: object,
    // Content shown when no items
    noItemsContent: JSX.Element,
    // Show folders options
    showFolderOptions?: boolean,
    // On renaming folder
    onRenameFolder?: (oldFolderName: string, newFolderName: string) => Promise<void>,
    // On deleting folder
    onDeleteFolder?: (folderPath: string) => Promise<void>,
    // Expand all items
    expandFolders?: boolean,
    // Fix scrollbar to left
    fixScrollBarLeft?: boolean,
    // Is disabled
    disabled?: boolean
}

/**
 * Sharepoint picker state
 */
interface ISharepointPickerState {
    // Is picker loaded ?
    loaded: boolean,
    // Available folders
    folders: Array<string>,
    // Selected item
    selectedItem: string | undefined,
    // Opened folders
    openedFolders: Map<string, boolean>,
    // Overing folder
    overingFolder: string,
    // Show or hide folder rename dialog
    showRenameFolderDialog: boolean,
    // Folder to rename
    folderToRename: string,
    // Folder to delete
    folderToDelete: string,
    // Name of renamed folder
    renamedFolderName: string,
    // Renaming a folder
    renamingFolder: boolean,
    // Show confirm delete dialog folder
    showConfirmDeleteFolderDialog: boolean,
    // Deleting a folder
    deletingFolder: boolean,
}

/**
 * Sharepoint file and folder picker
 */
class SharepointPicker extends React.Component<ISharepointPickerProps, ISharepointPickerState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    constructor(props: ISharepointPickerProps) {
        super(props);
        this.state = {
            loaded: true,
            folders: new Array<string>(),
            selectedItem: undefined,
            openedFolders: new Map<string, boolean>(),
            overingFolder: "",
            showRenameFolderDialog: false,
            folderToRename: "",
            folderToDelete: "",
            renamedFolderName: "",
            renamingFolder: false,
            showConfirmDeleteFolderDialog: false,
            deletingFolder: false
        }
    }

    /**
     * Component initialization
     */
    async componentDidMount() {
        await this.refreshFolders();
        if (this.props.expandFolders) this.expandAllFolders();
        if ((!this.props.onlyFolders && !!this.props.selectedFile)
            || (this.props.onlyFolders && !!this.props.selectedFolder)) {
            this.setState({
                selectedItem: this.props.onlyFolders ?
                    this.props.selectedFolder : this.props.selectedFile?.imageLink
            }, () => this.focusSelectedItem());
        }
    }

    /**
     * When component has updated
     * @param prevProps previous props
     * @param prevState previous state
     */
    async componentDidUpdate(prevProps: Readonly<ISharepointPickerProps>, prevState: Readonly<ISharepointPickerState>) {
        // When selected item has changed
        if (!!this.props.handleSelect && this.state.selectedItem !== undefined && prevState.selectedItem !== this.state.selectedItem)
            this.props.handleSelect(this.state.selectedItem, this.getFolderItems(this.state.selectedItem));

        // Refresh folders list if items list has updated
        if (prevProps.items !== this.props.items)
            this.refreshFolders();
    }

    /**
     * Allow component to render
     * @param nextProps
     * @param nextState
     */
    shouldComponentUpdate(nextProps: Readonly<ISharepointPickerProps>, nextState: Readonly<ISharepointPickerState>): boolean {
        return this.state.loaded || nextState.loaded;
    }

    /**
     * Component destruction
     */
    componentWillUnmount() {
        this.setState({loaded: false});
    }

    /**
     * Refresh folders
     */
    refreshFolders = () => {
        // Retrieve available folders
        this.setState({
            folders: [blobsRootFolderName, ...this.props.items.filter(item => item.indexOf(".") === -1)]
        });
    }

    /**
     * Expand all folders to show all items
     */
    expandAllFolders = () => {
        // Map copy
        const openedFolders = new Map<string, boolean>();
        // Set all folders opened
        this.state.folders.forEach(folder => openedFolders.set(folder, true));
        this.setState({openedFolders});
    }

    /**
     * Scroll to current element
     */
    focusSelectedItem = () => {
        // Expand selected item folders hierarchy
        this.expandFoldersToItem();
        // Sharepoint picker ref
        const picker = document.getElementById("sharepoint_picker");
        // Find selected element
        const ref = document.querySelector("li[aria-selected=true]");
        if (!!picker && !!ref) {
            // Scroll to top
            picker.scrollTo(0, 0);
            // Value to scroll at
            const value = ref.getBoundingClientRect().top - picker.getBoundingClientRect().top - ref.scrollHeight;
            // Scroll to value
            picker.scrollTo(0, value);
        }
    }

    /**
     * Get all files names contained in a folder
     * @param folder folder
     */
    getFolderFileNames = (folder: string): Array<string> => {
        const items = this.getFolderItems(folder);
        // Return all files
        return items.filter(item => item.indexOf(".") !== -1);
    }

    /**
     * Get all subfolders of a folder
     * @param folder folder
     */
    getFolderSubFolders = (folder: string): Array<string> => {
        const items = this.getFolderItems(folder);
        // Return all subfolders
        return items.filter(item => item.indexOf(".") === -1);
    }

    /**
     * Get all items of a folder
     * @param folder
     */
    getFolderItems = (folder: string): Array<string> => {
        const usedFolder = folder.endsWith("/") ? folder : `${folder}/`
        // Number of slashes of folder
        const folderSlashes = usedFolder.split("/").length - 1;
        // Items
        const items = new Array<string>();
        // Retrieve items
        this.props.items.forEach(item => {
            if (item !== usedFolder && !items.includes(item) && item.length > 0) {
                // Number of slashes of item
                const itemSlashes = item.split("/").length - (item.endsWith("/") ? 2 : 1);
                if (item.startsWith(usedFolder) && folderSlashes === itemSlashes)
                    items.push(item);
            }
        });
        return items;
    }

    /**
     * Update opened folders list
     * @param folderId folder id
     */
    updateFolderState = (folderId: string) => {
        // Map copy
        const openedFolders = new Map<string, boolean>(this.state.openedFolders);
        // Update folder state
        openedFolders.set(folderId, !openedFolders.get(folderId) ?? true);
        this.setState({openedFolders});
    }

    /**
     * Open parent folders of selected item
     */
    expandFoldersToItem = () => {
        if (!this.state.selectedItem) return;
        // Selected item folders
        const folders = new Array<string>();
        let item = this.state.selectedItem;
        // Retrieve item folders hierarchy
        while (item.indexOf("/") !== -1) {
            const lastSlashPos = item.lastIndexOf("/");
            const folder = item.substring(0, lastSlashPos + 1);
            if (!folders.includes(folder))
                folders.push(folder);
            item = item.substring(0, lastSlashPos);
        }
        // Map copy
        const openedFolders = new Map<string, boolean>(this.state.openedFolders);
        // Set all folders opened
        folders.forEach(folder => openedFolders.set(folder, true));
        this.setState({openedFolders});
    }

    /**
     * Handle item selection
     * @param item item as folder or file
     */
    handleItemSelection = (item: string) => {
        // If item is a folder
        const isFolder = item.indexOf(".") === -1;
        // Go to sub folder if item is already selected or in file selection mode
        if (isFolder) {
            // Update folder open state
            this.updateFolderState(item);
        }
        // Set selected item depending on selection mode
        if ((this.props.onlyFolders && isFolder) || !isFolder)
            this.setState({selectedItem: item});
    }

    /**
     * Get item icon depending on extension
     */
    getItemIcon = (item: string) => {
        return item.indexOf(".") === -1 ?
            <FolderIcon className={"item_icon"}/>
            :
            <FilesPictureColoredIcon className={"item_icon"}/>;
    }

    /**
     * Unselect item
     */
    unselectItem = () => {
        if (!!this.props.handleSelect) this.props.handleSelect("", new Array<string>());
        this.setState({selectedItem: undefined});
    }

    /**
     * Rename a folder
     */
    renameFolder = async () => {
        if (!this.props.onRenameFolder) return;
        this.setState({renamingFolder: true});
        const oldFolderName = this.state.folderToRename;
        let newFolderName = oldFolderName.substring(0, oldFolderName.length - 1);
        newFolderName = newFolderName.substring(0, newFolderName.lastIndexOf("/") + 1);
        newFolderName += this.state.renamedFolderName;
        let stateUpdate: Partial<ISharepointPickerState> = {
            renamingFolder: false,
            folderToRename: "",
            renamedFolderName: "",
            showRenameFolderDialog: false,
        };
        await this.props.onRenameFolder(oldFolderName, newFolderName);
        const openedFolders = new Map<string, boolean>();
        this.state.openedFolders.forEach((value, key) => {
            openedFolders.set(key.replace(oldFolderName, newFolderName), value);
        })
        stateUpdate = {...stateUpdate, openedFolders}
        this.setState(stateUpdate as any);
    }

    /**
     * Delete a folder
     */
    deleteFolder = () => {
        this.setState({deletingFolder: true});
        return new Promise<void>((resolve, reject) => {
            deleteBlobFolder(this.state.folderToDelete)
                .then(async success => {
                    if (success && !!this.props.onDeleteFolder)
                        await this.props.onDeleteFolder(this.state.folderToDelete);
                    else reject("Can't delete folder")
                    this.setState({
                        deletingFolder: false,
                        folderToDelete: "",
                        showConfirmDeleteFolderDialog: false
                    });
                })
        })
    }

    /**
     * Handle click on rename folder button
     * @param folderPath folder path
     * @param folderName folder name
     */
    handleRenameFolderButton = (folderPath: string, folderName: string) => {
        this.setState({
            folderToRename: folderPath,
            showRenameFolderDialog: true,
            renamedFolderName: folderName
        });
    }

    /**
     * Handle click on delete folder button
     * @param folderPath folder path
     */
    handleDeleteFolderButton = (folderPath: string) => {
        this.setState({
            folderToDelete: folderPath,
            showConfirmDeleteFolderDialog: true,
        });
    }

    /**
     * Render component
     */
    render() {
        // Wait to render after everything is loaded
        if (!this.state.loaded)
            return <Flex fill style={{width: "50%"}} hAlign={"center"} vAlign={"center"}><Loader/></Flex>;

        return this.props.items.length > 0 ?
            <Flex fill column styles={{position: "relative"}} className={"overflow-hidden"}>
                <Flex fill column id={"sharepoint_picker"} className={"overflow-scroll"}
                      styles={{...this.props.styles, direction: this.props.fixScrollBarLeft ? "rtl" : "ltr"}}
                      onClick={this.unselectItem}>
                    {this.renderItem(blobsRootFolderName)}
                </Flex>
                {this.props.showFolderOptions &&
                    <>
                        {this.renderFolderRenameDialog()}
                        {this.renderConfirmDeleteFolderDialog()}
                    </>
                }
                {this.props.disabled &&
                    <Flex fill styles={{position: "absolute", backgroundColor: "black", opacity: "0.5"}}/>
                }
            </Flex>
            :
            this.props.noItemsContent
    }

    /**
     * Render item
     * @param url item
     */
    renderItem = (url: string) => {
        // Check if item is a folder
        const isFolder = url.indexOf(".") === -1;
        // If it's a folder
        if (isFolder) {
            // Item as folder
            const folder = url;
            // Folder items
            let folderItems = this.props.onlyFolders ?
                this.getFolderSubFolders(folder) : this.getFolderItems(folder);
            // Folder name
            let folderName = folder === blobsRootFolderName ? languageObject.translate("MyImages") : folder;//folder.substring(0, folder.length - 1);
            folderName = folderName.substring(folderName.lastIndexOf("/") + 1);
            const isOpen = this.state.openedFolders.get(folder) ?? false;
            return (
                <Accordion
                    className={"sharepoint_picker_item"}
                    styles={{direction: "ltr"}}
                    key={folder + "_accordion"}
                    activeIndex={isOpen ? [0] : undefined}
                    panels={[{
                        title: {
                            id: folder + "_id",
                            styles: {padding: "0 !important"},
                            key: folder + "_title",
                            content: (
                                <ListItem
                                    onMouseEnter={() => this.setState({overingFolder: folder})}
                                    onMouseLeave={() => this.setState({overingFolder: ""})}
                                    selectable={true}
                                    className={"list_item noselect"}
                                    styles={{height: "48px"}}
                                    index={0} key={folder + "_listItem"}
                                    media={
                                        <Flex vAlign={"center"} hAlign={"start"}>
                                            {this.getItemIcon(folder)}
                                        </Flex>
                                    }
                                    header={{
                                        className: "overflow-hidden",
                                        content: (
                                            <Flex vAlign={"center"} gap={"gap.small"} className={"overflow-hidden"}>
                                                <Text content={folderName} truncated/>
                                                {this.props.showFolderOptions && this.state.overingFolder === folder && folder !== blobsRootFolderName &&
                                                    <MenuButton
                                                        trigger={
                                                            <Button icon={<MoreIcon outline/>} iconOnly text
                                                                    title={languageObject.translate("FolderOptions")}
                                                                    onClick={e => e.stopPropagation()}/>
                                                        }
                                                        menu={[
                                                            {
                                                                key: "action_rename_" + folder,
                                                                content: languageObject.translate("Rename"),
                                                                icon: <EditIcon outline/>,
                                                                onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    this.handleRenameFolderButton(folder, folderName);
                                                                }
                                                            },
                                                            {
                                                                key: "action_delete_" + folder,
                                                                content: languageObject.translate("Delete"),
                                                                icon: <TrashCanIcon outline/>,
                                                                onClick: (e) => {
                                                                    e.stopPropagation();
                                                                    this.handleDeleteFolderButton(folder);
                                                                }
                                                            },
                                                        ]}
                                                    />
                                                }
                                                {folderItems.length > 0 ?
                                                    <FlexItem push>
                                                        {isOpen ? <ChevronDownIcon outline/> :
                                                            <ChevronEndIcon outline/>}
                                                    </FlexItem>
                                                    :
                                                    null
                                                }
                                            </Flex>
                                        )
                                    }}
                                    selected={this.state.selectedItem === folder}
                                />
                            ),
                            onClick: e => {
                                e.stopPropagation();
                                this.handleItemSelection(folder)
                            }
                        },
                        content: {
                            key: folder + "_content",
                            content: folderItems.map(subItem => this.renderItem(subItem)),
                        }
                    }]}
                />
            )
        } else {
            // Item as file
            const file = url;
            return (
                <ListItem
                    selectable={true}
                    className={"list_item noselect"}
                    index={0} key={file + "_listItem"}
                    media={
                        <Flex vAlign={"center"} hAlign={"start"}>
                            {this.getItemIcon(file)}
                        </Flex>
                    }
                    header={{
                        className: "overflow-hidden",
                        content: (
                            <Flex vAlign={"center"} gap={"gap.small"} className={"overflow-hidden"}>
                                <Text content={file.substring(file.lastIndexOf("/") + 1)} truncated/>
                            </Flex>
                        )
                    }}
                    selected={this.state.selectedItem === file}
                    onClick={e => {
                        e.stopPropagation();
                        this.handleItemSelection(file)
                    }}
                />
            )
        }
    }

    /**
     * Render rename folder dialog
     */
    renderFolderRenameDialog = () => {
        // Retrieve old folder name from folder path
        let oldFolderName = this.state.folderToRename.substring(0, this.state.folderToRename.length - 1);
        oldFolderName = oldFolderName.substring(oldFolderName.lastIndexOf("/") + 1);

        return (
            <Dialog
                styles={{maxWidth: "600px"}}
                open={this.state.showRenameFolderDialog}
                confirmButton={{
                    disabled: this.state.renamedFolderName.length === 0 ||
                        this.state.renamedFolderName === oldFolderName ||
                        this.state.renamingFolder,
                    loading: this.state.renamingFolder,
                    icon: <AcceptIcon outline/>,
                    content: languageObject.translate("Confirm"),
                    onClick: this.renameFolder
                }}
                content={
                    <Flex column>
                        <EditInput
                            disabled={this.state.renamingFolder}
                            required
                            max={50}
                            errorMessage={this.state.renamedFolderName === oldFolderName ? languageObject.translate("PleaseSelectOtherName") : ""}
                            label={languageObject.translate("FolderName")}
                            value={this.state.renamedFolderName}
                            setValue={value => (value.length === 0 || value.match(/^[a-zA-Z0-9-_ ]+$/)) ?
                                this.setState({renamedFolderName: value}) : null}
                        />
                        <Text styles={{color: "gray", marginTop: "2px"}}
                              content={languageObject.translate("AvailableCharsFolderName")}/>
                    </Flex>
                }
                header={languageObject.translate("RenameFolder")}
                headerAction={{
                    icon: <CloseIcon outline/>,
                    title: 'Close',
                    onClick: () => this.setState({showRenameFolderDialog: false}),
                }}/>
        )
    }

    /**
     * Render confirm delete dialog
     */
    renderConfirmDeleteFolderDialog = () => {
        return (
            <ConfirmDialog
                title={languageObject.translate("DeleteFolder")}
                content={languageObject.translate("AreYouSureDeleteFolder")}
                loading={this.state.deletingFolder}
                onConfirm={this.deleteFolder}
                onCancel={() => this.setState({showConfirmDeleteFolderDialog: false, folderToDelete: ""})}
                show={this.state.showConfirmDeleteFolderDialog}/>
        )
    }
}

export default SharepointPicker;